import {
  useMemo,
  useEffect,
  useCallback,
  useRef,
  type RefObject,
} from 'react'

import { AuthType } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import { type AgGridReact } from 'ag-grid-react'
import GridTooltip from 'components/commonparts/GridTooltip/GridTooltip'
import { AG_GRID_LOCALE } from 'utils/agGridLocale'

import { useGridFormatters } from './useGridFormatters'

import type {
  GridReadyEvent,
  ColumnMenuTab,
  ColumnState,
  GetMainMenuItemsParams,
  MenuItemDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  ColDef,
} from 'ag-grid-community'

interface GridCommonProps {
  gridRef: RefObject<AgGridReact>
  isLoading?: boolean
  isUpdating?: boolean
  storageKey?: string
  preferColDefWidth?: boolean
}

/**
 * ag-Gridの基本的な設定を提供するカスタムフック
 */
export const useGridBase = ({
  gridRef,
  isLoading,
  isUpdating = false,
  storageKey,
  preferColDefWidth = true,
}: GridCommonProps) => {
  const { loginUserInfo } = useAuthContext()
  const gridReady = useRef(false)
  const formatters = useGridFormatters()

  /**
   * GridCommon
   */
  const localeText = useMemo<{
    [key: string]: string
  }>(() => AG_GRID_LOCALE, [])

  /**
   * GridCommon
   */
  const autoSizeStrategy = useMemo<
    SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy | undefined
    >(() => {
      try {
        const masterKey = 'gridColumnStates'
        const savedAll = typeof window !== 'undefined' ? localStorage.getItem(masterKey) : null
        if (savedAll) {
          const obj = JSON.parse(savedAll) as Record<string, unknown>
          // キーは useGridBase 内で構築される columnStateKey と一致させる
          const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
          const key = (typeof (storageKey) === 'string' && storageKey.length > 0)
            ? storageKey
            : `gridColumnState:${path}`
          if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
            // 既に保存済みの状態がある場合、autoSize を無効化して上書きを防ぐ
            return undefined
          }
        }
      } catch {
        // noop
      }
      // 優先度: 明示的にカラム幅を優先する設定 → autoSizeを無効
      if (preferColDefWidth) return undefined
      // それ以外は既定でセル内容にフィット
      return { type: 'fitCellContents' } as SizeColumnsToContentStrategy
    }, [storageKey, preferColDefWidth])

  /**
   * GridCommon
   */
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
  
    // manage loading by grid option (v34)
    api.setGridOption('loading', !!(isLoading || isUpdating))
  }, [isLoading, isUpdating])

  /**
   * GridCommon
   */
  const onGridReady = ((event: GridReadyEvent<any, any>) => {
    const columnStateKey = storageKey || (() => {
      try {
        const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
        return `gridColumnState:${path}`
      } catch {
        return 'gridColumnState:unknown'
      }
    })()
    const masterStateKey = 'gridColumnStates'
    // console.log('fook! onGridReady', event.api)
    // setGridApi(event.api)
    gridReady.current = true
    // setGridReady(true);

    event.api.updateGridOptions({
      // localeText : localeText,
      defaultColDef,
      hidePaddedHeaderRows: true,
      rowNumbers: true,
      cellSelection: {
        enableHeaderHighlight: true,
        handle: {
          mode: 'fill',
          direction: 'xy'
        }
      },
      animateRows: true,
      tooltipShowDelay: 0,
      tooltipHideDelay: 3000,
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true,
    },
      // autoSizeStrategy:autoSizeStrategy,
      rowDragManaged: true,
      pagination: false,
      rowHeight: 36,
      allowContextMenuWithControlKey: true,
      rowClassRules: getRowClassRules(),
      // loadingOverlayComponent : LoadingOverlay,
      overlayNoRowsTemplate:
        '<span aria-live:"polite" aria-atomic:"true",pos>条件に合致する商品が見つかりません</span>',
      suppressLastEmptyLineOnPaste: true,
      // onGridPreDestroyed: () => {
      //   console.log('fook! onGridPreDestroyed')
      // },
      // reactiveCustomComponents:true,
      // onFilterChanged:onFilterChanged,
    })
      // 保存済みのカラム表示設定を適用（幅・並び順・表示/非表示 など）
      const applySavedColumnState = () => {
        try {
          const savedAll = localStorage.getItem(masterStateKey)
          if (savedAll) {
            const obj = JSON.parse(savedAll) as Record<string, ColumnState[]>
            const state = obj?.[columnStateKey]
            if (Array.isArray(state) && state.length > 0) {
              event.api.applyColumnState({ state, applyOrder: true })
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        }
      }

      // 即時適用
      applySavedColumnState()
      // データ初回描画後にも再適用（autoSize等で上書きされるのを防ぐ）
      event.api.addEventListener('firstDataRendered', () => {
        applySavedColumnState()
      })
    // 以下のinitに設定されているプロパティは、DOMでしか設定できない模様
    // gridRef.current.api.setGridOption('localeText' as ManagedGridOptionKey, localeText)
    // gridRef.current.api.setGridOption('autoSizeStrategy' as ManagedGridOptionKey, autoSizeStrategy)
    // gridRef.current.api.setGridOption('loadingOverlayComponent' as ManagedGridOptionKey, LoadingOverlay)
    // gridRef.current.api.setGridOption('reactiveCustomComponents' as ManagedGridOptionKey, true)

  });

  /**
   * GridCommon
   * 編集可能かどうかを判定する
   */
  const isEnableEdit = useCallback(
    (colEditable = true): boolean => {
      if (!colEditable) return false
      if (!loginUserInfo) return false
      if (loginUserInfo.authType === AuthType.ReadOnly) return false
      return true
    },
    [loginUserInfo, loginUserInfo?.authType]
  )

  /**
   * GridCommon
   */
  const getCellClassRules = () => ({
    'error-cell': (params: any) => {
      const rowData = params.data as typeof params.data
      const { field } = params.colDef
      const hasError = rowData?.errors && Array.isArray(rowData.errors[field]) && rowData.errors[field].length > 0
      return hasError || false
    },
    'current-updated-cell': (params: any) => {
      const rowData = params.data as typeof params.data
      const { field } = params.colDef
      return (
        rowData?.isEdited &&
        (!rowData.errors?.[field] || rowData.errors[field].length === 0) &&
        rowData.editedFields?.has(field)
      )
    },
    'editable-cell': (params: any) => {
      if (!isEnableEdit(params.colDef.editable)) return false
      const { editable } = params.colDef
      let iseditable = false
      if (typeof editable === 'function') {
        iseditable = editable(params)
      } else {
        iseditable = editable
      }
      return iseditable
    },
  })

  const getRowClassRules = () => ({
    // 'pinned-row-custom': (params: any) => params.node.rowPinned === 'top' || params.node.rowPinned === 'bottom'
  })

  /**
   * GridCommon
   */
  const tooltipValue = useCallback((params: any) => {
    const { value, data, colDef } = params
    const { field } = colDef

    if (data && data?.errors && Array.isArray(data.errors[field]) && data.errors[field].length > 0) {
      return `${data.errors[field].join(', ')}`
    }
    return value !== null && value !== undefined ? String(value) : 'No Value'
  }, [])

  const masterStateKey = 'gridColumnStates'

  const customMainMenuItems = useCallback((params: GetMainMenuItemsParams): any => {
    const menuItems: (MenuItemDef | string)[] = params.defaultItems.slice()

    menuItems.splice(1, 0, {
      name: '列非表示',
      subMenu: [
        {
          name: '左側の隠れた列を表示',
          action: () => {
            const allColumns = params.api.getColumns()
            if (!allColumns || !params.column) return
            const currentIndex = allColumns.findIndex((col) => col.getColId() === params.column!.getColId())
            for (let i = currentIndex - 1; i >= 0; i -= 1) {
              if (!allColumns[i].isVisible()) {
                params.api.setColumnsVisible([allColumns[i].getColId()], true)
                break
              }
            }
          },
        },
        {
          name: '右側の隠れた列を表示',
          action: () => {
            const allColumns = params.api.getColumns()
            if (!allColumns || !params.column) return
            const currentIndex = allColumns.findIndex((col) => col.getColId() === params.column!.getColId())
            for (let i = currentIndex + 1; i < allColumns.length; i += 1) {
              if (!allColumns[i].isVisible()) {
                params.api.setColumnsVisible([allColumns[i].getColId()], true)
                break
              }
            }
          },
        },
        {
          name: 'この列を非表示',
          action: () => {
            params.api.setColumnsVisible([params.column!.getColId()], false)
          },
        },
      ],
    } as MenuItemDef)

    menuItems.splice(2, 0, {
      name: '表示設定の保存',
      action: () => {
        const columnState: ColumnState[] = params.api.getColumnState()
        try {
          const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
          const key = storageKey || `gridColumnState:${path}`
          const savedAll = localStorage.getItem(masterStateKey)
          const obj = savedAll ? JSON.parse(savedAll) : {}
          obj[key] = columnState
          localStorage.setItem(masterStateKey, JSON.stringify(obj))
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        }
      },
    } as MenuItemDef)

    const pinSubMenuIndex = menuItems.findIndex((item) => item === 'pinSubMenu')
    if (pinSubMenuIndex >= 0) {
      menuItems[pinSubMenuIndex] = {
        name: '列固定',
        subMenu: [
          {
            name: 'ピンなし',
            action: () => params.api.setColumnsPinned([params.column!.getColId()], null),
          },
          {
            name: '左にピン',
            action: () => params.api.setColumnsPinned([params.column!.getColId()], 'left'),
          },
          {
            name: '右にピン留め',
            action: () => params.api.setColumnsPinned([params.column!.getColId()], 'right'),
          },
        ],
      } as MenuItemDef
    }

    const autoSizeIndex = menuItems.findIndex((item) => item === 'autoSizeThis')
    if (autoSizeIndex >= 0) {
      menuItems.splice(autoSizeIndex, 1)
    }

    const autoSizeAllIndex = menuItems.findIndex((item) => item === 'autoSizeAll')
    if (autoSizeAllIndex >= 0) {
      menuItems.splice(autoSizeAllIndex, 1)
    }

    const resetColumnIndex = menuItems.findIndex((item) => item === 'resetColumns')
    if (resetColumnIndex >= 0) {
      menuItems[resetColumnIndex] = {
        name: '列をリセット',
        action: () => {
          params.api.resetColumnState()
          try {
            const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown'
            const key = storageKey || `gridColumnState:${path}`
            const savedAll = localStorage.getItem(masterStateKey)
            if (savedAll) {
              const obj = JSON.parse(savedAll) as Record<string, ColumnState[]>
              delete obj[key]
              const keysLeft = Object.keys(obj)
              if (keysLeft.length > 0) {
                localStorage.setItem(masterStateKey, JSON.stringify(obj))
              } else {
                localStorage.removeItem(masterStateKey)
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
          }
        },
      }
    }
    return menuItems
  }, [storageKey])

  /**
   * GridCommon
   */
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      suppressHeaderMenuButton: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 60,
      sortable: true,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          { filter: 'agTextColumnFilter' },
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
      menuTabs: ['filterMenuTab', 'generalMenuTab'] as ColumnMenuTab[],
      mainMenuItems: customMainMenuItems,
      tooltipValueGetter: tooltipValue,
      tooltipComponent: GridTooltip,
      cellClassRules: getCellClassRules(),
    }),
    [customMainMenuItems]
  )

  /**
   * GridCommon
   * 統一的な日付フィルターの比較関数
   */
  const dateTreeSetFilterParams = {
    treeList: true,
    treeListPathGetter: (value: any) => {
      if (!value) return null
      const d = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(d.getTime())) return null
      const yyyy = String(d.getFullYear())
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return [yyyy, mm, dd] // Year > Month > Day
    },
    comparator: (a: any, b: any) => {
      const dateA = a instanceof Date ? a : new Date(a);
      const dateB = b instanceof Date ? b : new Date(b);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
  
      if (timeA === timeB) {
        return 0;
      }
      return timeA < timeB ? -1 : 1;
    },
    closeOnApply: true,
    defaultToNothingSelected: true, 
    applyMiniFilterWhileTyping: true,
    excelMode: 'mac',
  }
  /**
   * GridCommon
   */
  const makeWeekDayChiled = useCallback(
    (fieldNamePrefix: string, colDefChild: ColDef, fieldNameSafix = ''): ColDef[] => {
      const childs: ColDef[] = []
      const weekDays = ['月', '火', '水', '木', '金', '土', '日']
      const weekDaysField = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      weekDays.forEach((day, index) => {
        const daycol = {
          ...colDefChild,
          headerName: day,
          field: `${fieldNamePrefix}${weekDaysField[index]}${fieldNameSafix}`,
        }
        childs.push(daycol)
      })
      return childs
    },
    []
  )

  /**
   * GridCommon
   * すべての行のデータを配列で取得する
   * get All Row data Array
   */
  const getRowData = useCallback((): any[] => {
    if (!gridRef.current?.api || gridRef.current.api.isDestroyed()) return []
    const rowData: any[] = []
    gridRef.current.api.forEachNode((node) => rowData.push(node.data))
    return rowData
  }, [])

  return {
    // formattersオブジェクトのプロパティを展開
    ...formatters,
    // GridBase固有のプロパティ
    localeText,
    autoSizeStrategy,
    onGridReady,
    isEnableEdit,
    getCellClassRules,
    getRowClassRules,
    tooltipValue,
    defaultColDef,
    customMainMenuItems,
    dateTreeSetFilterParams,
    makeWeekDayChiled,
    getRowData,
  }
}